import { useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  Radio,
  Stack,
  Text,
} from '@mantine/core'
import { IconAlertCircle, IconAlertTriangle } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { useOrpc } from '@/hooks/useClient'
import type { CpgDocumentDetail } from './hooks'

type PredecessorChoice = 'auto' | 'none' | 'other'

interface ActivateModalProps {
  doc: CpgDocumentDetail
  onClose: () => void
  onActivated: () => void | Promise<void>
}

/**
 * The registration gate: confirms what activation will do — which active document gets
 * superseded (auto-detected by source identity, overridable for cross-artifact succession
 * like a renamed PDF edition), and an explicit opt-in when no families are assigned.
 */
export function ActivateModal({ doc, onClose, onActivated }: ActivateModalProps) {
  const orpc = useOrpc()
  const auto = doc.activationPredecessor
  const [choice, setChoice] = useState<PredecessorChoice>(auto ? 'auto' : 'none')
  const [otherId, setOtherId] = useState<number | ''>('')
  const [allowNoFamilies, setAllowNoFamilies] = useState(false)

  const activate = useMutation(
    orpc.activateCpgDocument.mutationOptions({ onSuccess: () => onActivated() }),
  )

  const noFamilies = doc.assignments.length === 0
  const supersedesDocumentId =
    choice === 'auto' ? undefined : choice === 'none' ? null : otherId === '' ? undefined : otherId
  const ready = (choice !== 'other' || otherId !== '') && (!noFamilies || allowNoFamilies)

  return (
    <Modal opened onClose={onClose} title={<Text fw={600}>Activate document</Text>} size="lg">
      <Stack gap="sm">
        <Text size="sm">
          <strong>{doc.title}</strong> (v{doc.version}) becomes <strong>active</strong> and enters
          the retrieval corpus.
        </Text>

        <Radio.Group
          label="Predecessor"
          description="The active document this one replaces — it becomes superseded in the same transaction"
          value={choice}
          onChange={(value) => setChoice(value as PredecessorChoice)}
        >
          <Stack gap="xs" mt="xs">
            {auto && (
              <Radio
                value="auto"
                label={`Auto-detected: ${auto.title} · v${auto.version} · #${auto.id}`}
              />
            )}
            <Radio value="none" label="None — activate standalone" />
            <Radio value="other" label="Another document (cross-artifact succession)" />
          </Stack>
        </Radio.Group>
        {choice === 'other' && (
          <NumberInput
            label="Predecessor document id"
            description="Must currently be active"
            value={otherId}
            onChange={(value) => setOtherId(typeof value === 'number' ? value : '')}
            min={1}
            className="w-64"
          />
        )}

        {noFamilies && (
          <Alert color="orange" icon={<IconAlertTriangle size={16} />}>
            <Stack gap="xs">
              <span>
                No families are assigned. The document stays searchable but will never surface
                through family matching.
              </span>
              <Checkbox
                label="Activate with no families"
                checked={allowNoFamilies}
                onChange={(e) => setAllowNoFamilies(e.currentTarget.checked)}
              />
            </Stack>
          </Alert>
        )}

        {activate.error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {activate.error.message}
          </Alert>
        )}

        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!ready}
            loading={activate.isPending}
            onClick={() => activate.mutate({ id: doc.id, supersedesDocumentId, allowNoFamilies })}
          >
            Activate
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
